import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { DropdownMenu } from '#pplx/frontend/aether/components/DropdownMenu/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { useAccountSwitcherEnabled } from '#pplx/frontend/apps/ask/account-switcher/useAccountSwitcherEnabled.js';
import { sidebarAccountMenuOpenedEvent } from '#pplx/frontend/apps/ask/events/sidebar/analytics.js';
import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { joinDisplayKeys } from '#pplx/frontend/apps/ask/keyboard-shortcuts-provider/keybindingDisplay.js';
import { useRegisteredShortcutDisplay } from '#pplx/frontend/apps/ask/keyboard-shortcuts-provider/useRegisteredShortcutDisplay.js';
import { SidebarIncognitoMenu } from '#pplx/frontend/apps/ask/layout-aux/sidebar-bottom/SidebarIncognitoMenu.js';
import { useMaxTierEnabled } from '#pplx/frontend/apps/ask/providers/hooks/useMaxTierEnabled.js';
import { BaseSettingsSidebarTabs, EnterpriseSettingsSidebarTabs, } from '#pplx/frontend/apps/ask/settings/config/TabConfigs.js';
import { accountPathToSettingsHash } from '#pplx/frontend/apps/ask/settings/settingsHash.js';
import { useKeyboardHintStyle } from '#pplx/frontend/apps/ask/user-settings/hooks/useKeyboardHintStyle.js';
import { useIsEnterpriseUser } from '#pplx/frontend/auth/useIsEnterpriseUser.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
const defaultType = 'default';
const LazyAccountMenuAppearanceSubmenu = Dynamic(async () => {
    const { AccountMenuAppearanceSubmenu } = await import('#pplx/frontend/apps/ask/layout-aux/sidebar-bottom/AccountMenuAppearanceSubmenu.js');
    return { default: AccountMenuAppearanceSubmenu };
});
const LazyAccountMenuLanguageSubmenu = Dynamic(async () => {
    const { AccountMenuLanguageSubmenu } = await import('#pplx/frontend/apps/ask/layout-aux/sidebar-bottom/AccountMenuLanguageSubmenu.js');
    return { default: AccountMenuLanguageSubmenu };
});
const LazyAccountMenuHelpSubmenu = Dynamic(async () => {
    const { AccountMenuHelpSubmenu } = await import('#pplx/frontend/apps/ask/layout-aux/sidebar-bottom/AccountMenuHelpSubmenu.js');
    return { default: AccountMenuHelpSubmenu };
});
const LazyAccountMenuSignOutItem = Dynamic(async () => {
    const { AccountMenuSignOutItem } = await import('#pplx/frontend/apps/ask/layout-aux/sidebar-bottom/AccountMenuSignOutItem.js');
    return { default: AccountMenuSignOutItem };
});
const LazyAccountSwitcher = Dynamic(async () => {
    const { AccountSwitcherMenuItems } = await import('#pplx/frontend/apps/ask/account-switcher/AccountSwitcherMenuItems.js');
    return { default: AccountSwitcherMenuItems };
});
const LazyAccountMenuMultiSignOut = Dynamic(async () => {
    const { AccountMenuMultiSignOut } = await import('#pplx/frontend/apps/ask/layout-aux/sidebar-bottom/AccountMenuMultiSignOut.js');
    return { default: AccountMenuMultiSignOut };
});
export const SidebarBottomPopover = ({ children, isExpanded, isOpen, onOpenChange, alignOffset, }) => {
    const { isMaxUpsellEnabled } = useMaxTierEnabled();
    const pathname = usePathname();
    const enterpriseOnboardingReturnTo = pathname === '/' ? 'pricing' : undefined;
    const openPaywallUpsell = usePaywallUpsell({ enterpriseOnboardingReturnTo });
    const { isMax } = useIsSubscribed();
    const isEnterprise = useIsEnterpriseUser();
    const showAccountSwitcher = useAccountSwitcherEnabled();
    const { $t } = useIntl();
    const { track } = useInstrumentation();
    const { isMobileStyle } = useAetherContext();
    const handleOpen = useCallback(() => {
        track(sidebarAccountMenuOpenedEvent({
            name: 'sidebar account menu opened',
            data: { source: isMobileStyle ? 'mobile_sidebar' : 'desktop_sidebar' },
        }));
    }, [isMobileStyle, track]);
    const hintStyle = useKeyboardHintStyle();
    const allSettingsDisplayKeysArr = useRegisteredShortcutDisplay('all-settings');
    const allSettingsDisplayKeys = useMemo(() => (allSettingsDisplayKeysArr ? joinDisplayKeys(allSettingsDisplayKeysArr, hintStyle) : ''), [allSettingsDisplayKeysArr, hintStyle]);
    const allSettingsHref = accountPathToSettingsHash(BaseSettingsSidebarTabs.Account.href);
    const menuItems = useMemo(() => [
        {
            icon: $icon('settings'),
            show: true,
            text: $t({
                defaultMessage: 'All settings',
                description: 'Menu item in the sidebar account popover linking to the main settings page',
                id: 'fe93e8a793',
            }),
            type: defaultType,
            href: allSettingsHref,
            target: '_self',
            rightElement: (_jsx(TextPrimitive, { as: "span", color: "light", size: "xs", children: allSettingsDisplayKeys })),
        },
        {
            icon: $icon('building'),
            show: isEnterprise,
            text: $t({
                defaultMessage: 'Organization settings',
                description: 'Menu item in the sidebar account popover linking to the organization settings page',
                id: '873b4e9906',
            }),
            type: defaultType,
            href: EnterpriseSettingsSidebarTabs.Organization.href,
            target: '_self',
        },
        {
            type: 'separator',
            show: isMaxUpsellEnabled && !isMax,
            className: '!border-b-soft !border-b !-mx-1 mb-1',
        },
        {
            icon: $icon('circle-arrow-up'),
            show: isMaxUpsellEnabled && !isMax,
            text: $t({
                defaultMessage: 'Upgrade plan',
                description: 'Menu item in the sidebar account popover to open the upgrade plan modal',
                id: '9eed57cdd2',
            }),
            type: defaultType,
            onClick: () => {
                openPaywallUpsell({
                    origin: ModalOrigin.SIDEBAR_BOTTOM_POPOVER,
                });
            },
        },
        {
            type: 'separator',
            show: true,
        },
        {
            icon: $icon('download'),
            show: true,
            text: $t({
                defaultMessage: 'Install apps',
                description: 'Menu item in the sidebar account popover linking to the platforms download page',
                id: 'e132fc1f9f',
            }),
            type: defaultType,
            href: '/platforms',
            target: '_self',
        },
    ], [
        $t,
        isMaxUpsellEnabled,
        openPaywallUpsell,
        isMax,
        isEnterprise,
        allSettingsDisplayKeys,
        allSettingsHref,
    ]);
    const { session, status } = useSession();
    return (_jsxs(DropdownMenu, { isOpen: isOpen, onToggle: (open) => {
            if (open && !isOpen) {
                handleOpen();
            }
            onOpenChange?.(open);
        }, triggerElement: children, maxHeightPx: 700, minWidthPx: 220, align: isExpanded ? 'start' : 'end', alignOffset: alignOffset, side: isExpanded ? 'top' : 'right', offset: isExpanded ? 0 : -2, children: [status === 'authenticated' && (_jsxs(_Fragment, { children: [_jsx(SidebarIncognitoMenu, { session: session }), _jsx(DropdownMenu.Separator, {})] })), showAccountSwitcher && (_jsxs(_Fragment, { children: [_jsx(LazyAccountSwitcher, {}), _jsx(DropdownMenu.Separator, {})] })), menuItems
                .filter((item) => item.show !== false)
                .map((item, index) => item.type === 'separator' ? (_jsx(DropdownMenu.Separator, {}, index)) : item.href ? (_jsx(DropdownMenu.LinkItem, { href: item.href, target: item.target, leadingAccessory: item.icon, trailingAccessory: item.rightElement, children: item.text }, index)) : (_jsx(DropdownMenu.Item, { leadingAccessory: item.icon, 
                // @ts-expect-error - mismatched type between legacy and aether
                onSelect: item.onClick, children: item.text }, index))), _jsx(DropdownMenu.Separator, {}), _jsx(LazyAccountMenuAppearanceSubmenu, {}), _jsx(LazyAccountMenuLanguageSubmenu, {}), _jsx(LazyAccountMenuHelpSubmenu, {}), _jsx(DropdownMenu.Separator, {}), showAccountSwitcher ? _jsx(LazyAccountMenuMultiSignOut, {}) : _jsx(LazyAccountMenuSignOutItem, {})] }));
};
//# sourceMappingURL=SideBarBottomPopover.js.map